import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTopBar } from '@/contexts/TopBarContext'
import { adminApi } from '@/api/admin'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { Plus, X, Check, UserCheck, Mail, MailCheck, Trash2, CreditCard, ChevronRight } from 'lucide-react'
import { usePagination } from '@/hooks/usePagination'
import { TablePagination } from '@/components/shared/TablePagination'

type TrainerFormData = {
  firstName: string
  lastName: string
  email: string
  phone: string
  isActive: boolean
  qualifiedCourseIds: string[]
}

const emptyForm = (): TrainerFormData => ({
  firstName: '', lastName: '', email: '', phone: '', isActive: true, qualifiedCourseIds: [],
})

function AddTrainerForm({
  onSubmit,
  onCancel,
  pending,
}: {
  onSubmit: (data: TrainerFormData) => void
  onCancel: () => void
  pending: boolean
}) {
  const [form, setForm] = useState<TrainerFormData>(emptyForm())

  const { data: courses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: () => adminApi.getCourses().then(r => r.data),
  })

  const toggleCourse = (id: string) => {
    setForm(f => ({
      ...f,
      qualifiedCourseIds: f.qualifiedCourseIds.includes(id)
        ? f.qualifiedCourseIds.filter(c => c !== id)
        : [...f.qualifiedCourseIds, id],
    }))
  }

  return (
    <Card className="p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900">New trainer</h3>
        <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600 cursor-pointer">
          <X size={16} />
        </button>
      </div>
      <form onSubmit={e => { e.preventDefault(); onSubmit(form) }} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="firstName">First name *</Label>
            <Input id="firstName" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lastName">Last name *</Label>
            <Input id="lastName" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} required />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email *</Label>
            <Input id="email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
          </div>
        </div>

        {courses.length > 0 && (
          <div className="space-y-2">
            <Label>Qualified to deliver</Label>
            <div className="grid grid-cols-2 gap-1.5 max-h-44 overflow-y-auto border border-gray-100 rounded-md p-3">
              {courses.map(c => (
                <label key={c.id} className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.qualifiedCourseIds.includes(c.id)}
                    onChange={() => toggleCourse(c.id)}
                    className="rounded border-gray-300 text-indigo-600"
                  />
                  <span className="text-sm text-gray-700">{c.name}</span>
                  {c.code && <span className="text-xs text-gray-400">({c.code})</span>}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={pending}>{pending ? 'Saving…' : 'Add trainer'}</Button>
        </div>
      </form>
    </Card>
  )
}

export default function TrainersPage() {
  useTopBar(<span className="text-sm font-medium text-gray-900">Trainers</span>)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showAdd, setShowAdd] = useState(false)
  const [showSeatsFull, setShowSeatsFull] = useState(false)
  const [invitedId, setInvitedId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const { data: trainers = [], isLoading } = useQuery({
    queryKey: ['trainers'],
    queryFn: () => adminApi.getTrainers().then(r => r.data),
  })
  const { paged: pagedTrainers, page, setPage, totalPages, total, pageSize, start } = usePagination(trainers)
  const { data: courses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: () => adminApi.getCourses().then(r => r.data),
  })
  const { data: seats } = useQuery({
    queryKey: ['trainer-seats'],
    queryFn: () => adminApi.getTrainerSeatInfo().then(r => r.data),
  })

  const courseMap = Object.fromEntries(courses.map(c => [c.id, c]))

  const createMutation = useMutation({
    mutationFn: (data: TrainerFormData) => adminApi.createTrainer({
      ...data,
      phone: data.phone || null,
    }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['trainers'] }); setShowAdd(false) },
  })

  const inviteMutation = useMutation({
    mutationFn: (id: string) => adminApi.inviteTrainer(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['trainers'] })
      queryClient.invalidateQueries({ queryKey: ['trainer-seats'] })
      setInvitedId(id)
      setTimeout(() => setInvitedId(null), 3000)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteTrainer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainers'] })
      queryClient.invalidateQueries({ queryKey: ['trainer-seats'] })
      setConfirmDeleteId(null)
      setDeleteError(null)
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message ?? err?.response?.data ?? 'Could not remove trainer.'
      setDeleteError(typeof msg === 'string' ? msg : 'Could not remove trainer.')
    },
  })

  const atLimit = seats ? seats.used >= seats.total : false

  const handleAddClick = () => {
    if (atLimit) {
      setShowSeatsFull(v => !v)
      setShowAdd(false)
    } else {
      setShowAdd(v => !v)
      setShowSeatsFull(false)
    }
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          {seats && (
            <p className="text-xs text-gray-400 mt-1">
              {seats.used} of {seats.total} trainer seat{seats.total !== 1 ? 's' : ''} used
              {(seats.addon > 0 || (seats.complimentary ?? 0) > 0) &&
                ` (${[`${seats.base} included`, seats.addon > 0 ? `${seats.addon} add-on` : null, (seats.complimentary ?? 0) > 0 ? `${seats.complimentary} complimentary` : null].filter(Boolean).join(' + ')})`}
            </p>
          )}
        </div>
        <Button size="sm" className="gap-2" onClick={handleAddClick}>
          <Plus size={16} /> Add trainer
        </Button>
      </div>

      {showSeatsFull && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-amber-900">Trainer seat limit reached</p>
              <p className="text-sm text-amber-800 mt-1">
                You're using all {seats?.total} trainer seat{seats?.total !== 1 ? 's' : ''} on your plan.
                To add more trainers, purchase additional seats from your billing settings.
              </p>
            </div>
            <button type="button" onClick={() => setShowSeatsFull(false)} className="text-amber-500 hover:text-amber-700 shrink-0 cursor-pointer">
              <X size={16} />
            </button>
          </div>
          <div className="mt-4 flex gap-2">
            <Button size="sm" className="gap-1.5" onClick={() => navigate('/admin/account?tab=billing')}>
              <CreditCard size={14} /> Go to billing settings
            </Button>
          </div>
        </div>
      )}

      {showAdd && (
        <AddTrainerForm
          onSubmit={data => createMutation.mutate(data)}
          onCancel={() => setShowAdd(false)}
          pending={createMutation.isPending}
        />
      )}

      {/* Mobile card list */}
      <div className="sm:hidden space-y-2">
        {isLoading && <p className="text-center text-gray-500 py-8">Loading…</p>}
        {!isLoading && trainers.length === 0 && (
          <div className="text-center py-12">
            <UserCheck size={36} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-500 font-medium">No trainers yet</p>
            <p className="text-sm text-gray-400 mt-1">Add your first trainer to start assigning them to bookings.</p>
          </div>
        )}
        {pagedTrainers.map(trainer => (
          <div key={trainer.id}>
            <Link
              to={`/admin/trainers/${trainer.id}`}
              className="block bg-white border border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:shadow-sm transition-all"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900">{trainer.firstName} {trainer.lastName}</p>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{trainer.email}</p>
                  {trainer.phone && <p className="text-xs text-gray-500">{trainer.phone}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {trainer.isActive
                    ? <Check size={14} className="text-green-500" />
                    : <span className="text-xs text-gray-400">Inactive</span>}
                  <ChevronRight size={15} className="text-gray-400" />
                </div>
              </div>
              {trainer.qualifiedCourseIds.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {trainer.qualifiedCourseIds.map(id => {
                    const c = courseMap[id]
                    return c ? (
                      <span key={id} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-700 border border-indigo-100">
                        {c.code ?? c.name}
                      </span>
                    ) : null
                  })}
                </div>
              )}
            </Link>
            {confirmDeleteId === trainer.id && (
              <div className="mt-1 rounded-lg bg-red-50 border border-red-100 px-4 py-3">
                <p className="text-sm font-medium text-red-800">Remove {trainer.firstName} {trainer.lastName}?</p>
                <p className="text-xs text-red-600 mt-0.5">
                  {trainer.hasAccount ? 'Their portal account will be deactivated. This cannot be undone.' : 'This cannot be undone.'}
                </p>
                {deleteError && <p className="text-xs text-red-700 font-medium mt-1">{deleteError}</p>}
                <div className="flex gap-2 mt-3">
                  <Button variant="outline" size="sm" onClick={() => { setConfirmDeleteId(null); setDeleteError(null) }}>Cancel</Button>
                  <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white border-0"
                    disabled={deleteMutation.isPending}
                    onClick={() => deleteMutation.mutate(trainer.id)}>
                    {deleteMutation.isPending ? 'Removing…' : 'Remove'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
        <TablePagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} start={start} onPage={setPage} />
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block">
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Qualifications</TableHead>
                <TableHead className="text-center">Active</TableHead>
                <TableHead className="text-center">Account</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={7} className="text-center text-gray-500 py-8">Loading…</TableCell></TableRow>
              )}
              {!isLoading && trainers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <UserCheck size={36} className="mx-auto text-gray-200 mb-3" />
                    <p className="text-gray-500 font-medium">No trainers yet</p>
                    <p className="text-sm text-gray-400 mt-1">Add your first trainer to start assigning them to bookings.</p>
                  </TableCell>
                </TableRow>
              )}
              {pagedTrainers.map(trainer => (
                <>
                  <TableRow
                    key={trainer.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/admin/trainers/${trainer.id}`)}
                  >
                    <TableCell>
                      <p className="font-medium text-gray-900">{trainer.firstName} {trainer.lastName}</p>
                    </TableCell>
                    <TableCell className="text-gray-600 text-sm">{trainer.email}</TableCell>
                    <TableCell className="text-gray-600 text-sm">{trainer.phone ?? '—'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {trainer.qualifiedCourseIds.length === 0 ? (
                          <span className="text-gray-400 text-xs">None</span>
                        ) : trainer.qualifiedCourseIds.map(id => {
                          const c = courseMap[id]
                          return c ? (
                            <span key={id} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-700 border border-indigo-100">
                              {c.code ?? c.name}
                            </span>
                          ) : null
                        })}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {trainer.isActive
                        ? <Check size={15} className="mx-auto text-green-500" />
                        : <span className="text-gray-300 text-xs">—</span>}
                    </TableCell>
                    <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                      {trainer.hasAccount ? (
                        <span title="Has portal account" className="inline-flex justify-center">
                          <MailCheck size={15} className="text-green-500" />
                        </span>
                      ) : (
                        <Button
                          variant="ghost" size="sm"
                          className="h-7 px-2 text-xs text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 gap-1"
                          disabled={inviteMutation.isPending}
                          title="Send portal invite"
                          onClick={() => inviteMutation.mutate(trainer.id)}
                        >
                          {invitedId === trainer.id ? <><MailCheck size={12} /> Sent!</> : <><Mail size={12} /> Invite</>}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Button
                        variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-red-600"
                        onClick={() => { setConfirmDeleteId(confirmDeleteId === trainer.id ? null : trainer.id); setDeleteError(null) }}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                  {confirmDeleteId === trainer.id && (
                    <TableRow key={`${trainer.id}-delete`}>
                      <TableCell colSpan={7} className="py-3 px-4 bg-red-50 border-t border-red-100">
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <p className="text-sm font-medium text-red-800">
                              Remove {trainer.firstName} {trainer.lastName}?
                            </p>
                            <p className="text-xs text-red-600 mt-0.5">
                              {trainer.hasAccount
                                ? 'Their portal account will be deactivated. This cannot be undone.'
                                : 'This cannot be undone.'}
                            </p>
                            {deleteError && <p className="text-xs text-red-700 font-medium mt-1">{deleteError}</p>}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button variant="outline" size="sm" onClick={() => { setConfirmDeleteId(null); setDeleteError(null) }}>
                              Cancel
                            </Button>
                            <Button
                              size="sm" className="bg-red-600 hover:bg-red-700 text-white border-0"
                              disabled={deleteMutation.isPending}
                              onClick={() => deleteMutation.mutate(trainer.id)}
                            >
                              {deleteMutation.isPending ? 'Removing…' : 'Remove trainer'}
                            </Button>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
          <TablePagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} start={start} onPage={setPage} />
        </Card>
      </div>
    </div>
  )
}
